import { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AutoTranslate } from '../hooks/useAutoTranslate';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Mail, ExternalLink, Phone } from 'lucide-react';
import { getFeaturedAnnouncements, getFeaturedAnnouncementsSettings } from '../services/api';
import SocialShareButtons from './SocialShareButtons';

// Single featured-announcement card. Holds its own expand/collapse state so
// each card in the carousel manages its "Läs mer" toggle independently.
const FeaturedCard = ({ announcement, index, focusedId, getCTAButton, t }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef(null);
  const announcementId = announcement.id || String(index);
  const shareUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/api/share/featured/${encodeURIComponent(announcementId)}`
      : '';
  const isFocused = focusedId && String(announcementId) === String(focusedId);

  // Detect actual visual overflow against `line-clamp-3` rather than guessing
  // from raw character count. Re-measures whenever the content or the
  // collapsed/expanded state changes (the latter is needed because the user
  // can collapse again and we still want to know it was originally overflowing).
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el || !announcement.content) {
      setIsOverflowing(false);
      return;
    }
    // When clamp is applied, scrollHeight > clientHeight indicates truncation.
    // When expanded (clamp removed), keep the previous overflow flag so the
    // "Visa mindre" button stays visible.
    if (!isExpanded) {
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    }
  }, [announcement.content, isExpanded]);

  return (
    <Card
      id={`featured-${announcementId}`}
      className={`card-hover overflow-hidden h-full border-0 shadow-xl transition-shadow flex flex-col ${
        isFocused ? 'ring-4 ring-amber-400 ring-offset-2' : ''
      }`}
    >
      {/* 1:1 Aspect Ratio Image */}
      <div className="aspect-square relative bg-gray-100 overflow-hidden">
        {announcement.image_url ? (
          <img
            src={announcement.image_url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-gray-300">
            <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
      </div>
      <CardContent className="p-4 flex flex-col flex-1">
        <h3 className="font-serif font-bold text-lg text-gray-900 mb-2 line-clamp-2">
          <AutoTranslate>{announcement.title}</AutoTranslate>
        </h3>
        {announcement.content && (
          <>
            <div
              ref={contentRef}
              className={`text-sm text-gray-600 ${!isExpanded ? 'line-clamp-3' : ''}`}
              data-testid={`featured-content-${announcementId}`}
            >
              <AutoTranslate>{announcement.content}</AutoTranslate>
            </div>
            {isOverflowing && (
              <button
                type="button"
                onClick={() => setIsExpanded((v) => !v)}
                className="mt-2 text-sm font-medium text-amber-700 hover:text-amber-800 flex items-center gap-1 transition-colors self-start"
                data-testid={`featured-readmore-${announcementId}`}
              >
                {isExpanded ? (
                  <>
                    {t('showLess') || 'Visa mindre'}
                    <ChevronUp className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    {t('readMore') || 'Läs mer'}
                    <ChevronDown className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </>
        )}
        {getCTAButton(announcement)}
        {/* Social share */}
        <div className="mt-4 pt-3 border-t border-gray-100">
          <SocialShareButtons
            url={shareUrl}
            title={announcement.title || ''}
            testIdPrefix={`share-featured-${announcementId}`}
          />
        </div>
      </CardContent>
    </Card>
  );
};

const FeaturedAnnouncementsSection = () => {
  const { isRTL, t } = useLanguage();
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ auto_scroll_enabled: true, auto_scroll_interval: 5 });
  const [isPaused, setIsPaused] = useState(false);
  const autoScrollRef = useRef(null);
  const sectionRef = useRef(null);
  const [focusedId, setFocusedId] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [announcementsData, settingsData] = await Promise.all([
          getFeaturedAnnouncements(),
          getFeaturedAnnouncementsSettings()
        ]);
        setAnnouncements(announcementsData);
        setSettings(settingsData);
      } catch (error) {
        console.error('Error loading featured announcements:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Deep-link: ?featured=<id> or #featured-<id> scrolls the section into view
  // and advances the carousel to the requested announcement.
  useEffect(() => {
    if (loading || announcements.length === 0 || typeof window === 'undefined') {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash || '';
    const hashId = hash.startsWith('#featured-') ? hash.slice('#featured-'.length) : null;
    const targetId = params.get('featured') || hashId;
    if (!targetId) return;

    const targetIndex = announcements.findIndex((a) => String(a.id) === String(targetId));
    if (targetIndex === -1) return;

    setCurrentIndex(targetIndex);
    setFocusedId(String(targetId));
    // Pause auto-scroll while the user inspects the deep-linked post
    setIsPaused(true);
    // Defer the scroll so the section has rendered in its final position
    setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, [loading, announcements]);

  // Track navigation state
  const hasMultipleMobile = announcements.length > 1;
  const hasMultipleDesktop = announcements.length > 3;

  // Navigation - moves one post at a time
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % announcements.length);
  }, [announcements.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? announcements.length - 1 : prev - 1));
  }, [announcements.length]);

  // Get 3 items starting from currentIndex (wrapping around)
  const getVisibleItems = () => {
    if (announcements.length <= 3) {
      return announcements;
    }
    const items = [];
    for (let i = 0; i < 3; i++) {
      items.push(announcements[(currentIndex + i) % announcements.length]);
    }
    return items;
  };

  // Auto-scroll effect - moves one post at a time
  useEffect(() => {
    if (!settings.auto_scroll_enabled || isPaused || announcements.length <= 1) {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
        autoScrollRef.current = null;
      }
      return;
    }

    autoScrollRef.current = setInterval(() => {
      // Always advance one post at a time
      setCurrentIndex((prev) => (prev + 1) % announcements.length);
    }, settings.auto_scroll_interval * 1000);

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
        autoScrollRef.current = null;
      }
    };
  }, [settings.auto_scroll_enabled, settings.auto_scroll_interval, isPaused, announcements.length]);

  // Pause on hover/touch
  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  if (loading || announcements.length === 0) {
    return null;
  }

  // Get CTA button content
  const getCTAButton = (announcement) => {
    if (announcement.cta_type === 'none') return null;

    const buttonText = announcement.cta_text || 'Läs mer';

    if (announcement.cta_type === 'contact') {
      return (
        <Link to="/contact#message-form">
          <Button size="sm" variant="outline" className="w-full mt-4 border-amber-300 text-amber-700 hover:bg-amber-50">
            <Phone className="w-4 h-4 mr-2" />
            <AutoTranslate>{buttonText}</AutoTranslate>
          </Button>
        </Link>
      );
    }

    if (announcement.cta_type === 'url' && announcement.cta_url) {
      return (
        <a href={announcement.cta_url} target="_blank" rel="noopener noreferrer">
          <Button size="sm" variant="outline" className="w-full mt-4 border-amber-300 text-amber-700 hover:bg-amber-50">
            <ExternalLink className="w-4 h-4 mr-2" />
            <AutoTranslate>{buttonText}</AutoTranslate>
          </Button>
        </a>
      );
    }

    if (announcement.cta_type === 'email' && announcement.cta_email) {
      return (
        <a href={`mailto:${announcement.cta_email}`}>
          <Button size="sm" variant="outline" className="w-full mt-4 border-amber-300 text-amber-700 hover:bg-amber-50">
            <Mail className="w-4 h-4 mr-2" />
            <AutoTranslate>{buttonText}</AutoTranslate>
          </Button>
        </a>
      );
    }

    return null;
  };

  // Render single announcement card (uses self-contained sub-component for expand/collapse state)
  const renderCard = (announcement, index) => (
    <FeaturedCard
      key={announcement.id || String(index)}
      announcement={announcement}
      index={index}
      focusedId={focusedId}
      getCTAButton={getCTAButton}
      t={t}
    />
  );

  return (
    <section 
      ref={sectionRef}
      className="py-12" 
      style={{ background: 'var(--theme-section-2)' }}
      data-testid="featured-announcements-section"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">
            <AutoTranslate>Aktuella tillkännagivanden</AutoTranslate>
          </h2>
          <p className="text-gray-600">
            <AutoTranslate>Viktiga meddelanden från församlingen</AutoTranslate>
          </p>
        </div>

        {/* Desktop View - 3 columns */}
        <div className="hidden md:block">
          <div className="relative">
            {/* Navigation Buttons - only show if more than 3 announcements */}
            {hasMultipleDesktop && (
              <>
                <button
                  onClick={goToPrevious}
                  className={`absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors ${isRTL ? 'rotate-180' : ''}`}
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={goToNext}
                  className={`absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors ${isRTL ? 'rotate-180' : ''}`}
                  aria-label="Next"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}

            {/* Cards Grid */}
            <div className="overflow-hidden">
              <div
                className="grid gap-6 transition-all duration-500"
                style={{
                  gridTemplateColumns: announcements.length < 3 
                    ? `repeat(${announcements.length}, minmax(0, 1fr))` 
                    : 'repeat(3, minmax(0, 1fr))',
                  maxWidth: announcements.length < 3 ? `${announcements.length * 33.33}%` : '100%',
                  margin: announcements.length < 3 ? '0 auto' : undefined,
                }}
              >
                {getVisibleItems().map((announcement, idx) => renderCard(announcement, currentIndex + idx))}
              </div>
            </div>

            {/* Dots Indicator - show current position */}
            {hasMultipleDesktop && (
              <div className="flex justify-center mt-6 gap-2">
                {announcements.map((announcement, idx) => (
                  <button
                    key={`desktop-dot-${announcement.id || idx}`}
                    onClick={() => setCurrentIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === currentIndex ? 'bg-amber-500 w-6' : 'bg-gray-300'
                    }`}
                    aria-label={`Go to item ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mobile View - 1 column with navigation */}
        <div className="md:hidden">
          <div className="relative">
            {/* Navigation Buttons - only show if more than 1 announcement */}
            {hasMultipleMobile && (
              <>
                <button
                  onClick={goToPrevious}
                  className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-600 ${isRTL ? 'rotate-180' : ''}`}
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={goToNext}
                  className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-600 ${isRTL ? 'rotate-180' : ''}`}
                  aria-label="Next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            {/* Single Card */}
            <div className="px-8">
              {renderCard(announcements[currentIndex % announcements.length], currentIndex)}
            </div>

            {/* Dots Indicator */}
            {hasMultipleMobile && (
              <div className="flex justify-center mt-4 gap-2">
                {announcements.map((announcement, idx) => (
                  <button
                    key={`mobile-dot-${announcement.id || idx}`}
                    onClick={() => setCurrentIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === currentIndex % announcements.length ? 'bg-amber-500 w-4' : 'bg-gray-300'
                    }`}
                    aria-label={`Go to announcement ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedAnnouncementsSection;
